import { z } from 'zod'
import {
  LienWaiverType,
  type FormTemplateAnnotationImageType,
  type FormTemplateAnnotationMetadataFieldType,
  type FormTemplateAnnotationType,
  type FormTemplateFont,
  type FormTemplateType,
  type SignatureAnnotationType,
  type StoredFileType,
  type TextAlignment,
} from '../enums.js'
import type { templateVariablesDocumentation } from '../template-variables/documentation.js'
import { LienWaiverProgressType } from '../utils/lien-waiver.js'
import { zodDaySchema } from '../utils/validation-zod.js'

const payloadPayAppSchema = z.object({
  type: z.literal('payApp'),
  payAppId: z.guid(),
  includeWatermarks: z.boolean().optional(),

  // If specified, we will ONLY add these to the pay app
  includeForms: z
    .object({
      conditionalLienWaiver: z.boolean(),
      allLowerTierLienWaivers: z.boolean(),
      allStoredMaterials: z.boolean(),
      allChangeOrders: z.boolean(),
      allAdditionalBackup: z.boolean(),
      allLegalRequirements: z.boolean(),
    })
    .optional(),

  // Backend option to force rendering of form values regardless of the PayApp status
  renderFormValues: z.boolean().optional(),

  // Backend option for printing default form values for annotations where no form value is saved.
  // This should be used only on downloaded or printed forms, as the values may otherwise appear
  // under user input fields in the UI.
  showDefaultVariables: z.boolean().optional(),

  // Whether to hide images that contain a signature or stamp
  hideImagesContainingSignatureOrStamp: z.boolean().optional(),
})
export type PayloadPayApp = z.infer<typeof payloadPayAppSchema>

const payloadInvoiceSchema = z.object({
  type: z.literal('invoice'),
  payAppId: z.guid(),
})
export type PayloadInvoice = z.infer<typeof payloadInvoiceSchema>

const payloadBackupSchema = z.object({
  type: z.literal('backup'),
  payAppId: z.guid(),
  includeForms: payloadPayAppSchema.shape.includeForms,
})
export type PayloadBackup = z.infer<typeof payloadBackupSchema>

const payloadLienWaiversSchema = z.object({
  type: z.literal('lienWaivers'),
  lienWaiverIds: z.array(z.guid()),
})
export type PayloadLienWaivers = z.infer<typeof payloadLienWaiversSchema>

const payloadLienWaiverSchema = z.object({
  type: z.literal('lienWaiver'),
  token: z.string(),
  renderFormValues: z.boolean().optional(),

  // Option to force rendering of form values regardless of the lien waiver status
  // This should be used only on downloaded or printed forms, as the values may otherwise appear
  // under user input fields in the UI.
  showDefaultVariables: z.boolean().optional(),
})
export type PayloadLienWaiver = z.infer<typeof payloadLienWaiverSchema>

const payloadVendorLienWaiverPreviewSchema = z.object({
  type: z.literal('vendorLienWaiverPreview'),
  lienWaiverId: z.guid(),
  vendorContractId: z.guid(),
  // The commitment selected in the dialog's "Commitment" dropdown. When a vendor has more than one
  // open commitment the selection isn't persisted on the lien waiver until Save/Send, so it's passed
  // here so the preview can render the linked commitment's amounts before then.
  vendorContractCommitmentId: z.guid().nullish(),
  lienWaiverType: z.enum(LienWaiverType),
  date: z.iso.datetime(),
  isJointCheck: z.boolean(),
  defaultAmount: z.number().optional(),
  defaultAmountText: z.string().optional(),
  defaultExceptionAmounts: z.string().optional(),
  defaultExceptionDates: z.string().optional(),
  subject: z.string().optional(),
  message: z.string().optional(),
  // Option for printing default form values for annotations where no form value is saved.
  // This should be used only on downloaded or printed forms, as the values may otherwise appear
  // under user input fields in the UI.
  showDefaultVariables: z.boolean().optional(),
  renderFormValues: z.boolean().optional(),
})
export type PayloadVendorLienWaiverPreview = z.infer<typeof payloadVendorLienWaiverPreviewSchema>

const payloadUnconditionalLienWaiverSchema = z.object({
  type: z.literal('unconditionalLienWaiver'),
  progressType: z.enum(LienWaiverProgressType).optional(),
  // The pay app is optional because a $0 unconditional lien waiver can be created without a pay app
  payAppId: z.guid().optional(),
  preSitelinePayAppId: z.guid().optional(),
  contractId: z.guid(),
  // The through date will be the same as the pay app's billing end if there is a pay app
  throughDate: z.iso.datetime(),

  // Whether to include the audit trail.
  // If not provided, it defaults to `contract.showAuditItemOnSignedPackages`
  // Pass `false` for notarization downloads.
  includeAudit: z.boolean().optional(),
})
export type PayloadUnconditionalLienWaiver = z.infer<typeof payloadUnconditionalLienWaiverSchema>

const payloadLegalDocumentsSchema = z.object({
  type: z.literal('legalDocuments'),
  projectId: z.guid(),
  companyId: z.guid(),
  legalDocumentIds: z.array(z.guid()),
  includeTableOfContents: z.boolean(),
})
export type PayloadLegalDocuments = z.infer<typeof payloadLegalDocumentsSchema>

const payloadVendorLegalDocumentsSchema = z.object({
  type: z.literal('vendorLegalDocuments'),
  projectId: z.guid(),
  companyId: z.guid(),
  legalDocumentIds: z.array(z.guid()),
  includeTableOfContents: z.boolean(),
})
export type PayloadVendorLegalDocuments = z.infer<typeof payloadVendorLegalDocumentsSchema>

const payloadLegalDocumentSchema = z.object({
  type: z.literal('legalDocument'),
  token: z.string(),
})
export type PayloadLegalDocument = z.infer<typeof payloadLegalDocumentSchema>

const payloadLegalRequirementSchema = z.object({
  type: z.literal('legalRequirement'),
  projectId: z.guid(),
  companyId: z.guid(),
  legalRequirementId: z.guid(),
  periodEnd: zodDaySchema.optional(), // YYYY-MM-DD
})
export type PayloadLegalRequirement = z.infer<typeof payloadLegalRequirementSchema>

const payloadIntegrationSyncSchema = z.object({
  type: z.literal('integrationSync'),
  projectId: z.guid(),
  legalDocumentIds: z.array(z.guid()),
  payAppIds: z.array(z.guid()),
  lienWaiverIds: z.array(z.guid()),
})
export type PayloadIntegrationSync = z.infer<typeof payloadIntegrationSyncSchema>

const payloadTemplatesPreviewSchema = z.object({
  type: z.literal('templatesPreview'),
  formTemplateVersionIds: z.array(z.guid()),
  formTemplateVariantIds: z.array(z.guid()).optional(),
  contractId: z.guid().optional(),
  payAppId: z.guid().optional(),

  // For previewing lien-waiver templates as seen by a vendor
  useVendorContext: z.boolean().optional(),
})
export type PayloadTemplatesPreview = z.infer<typeof payloadTemplatesPreviewSchema>

const payloadLienRightsMilestoneSchema = z.object({
  type: z.literal('lienRightsMilestone'),
  metadataId: z.guid(),
  // Backend option to force rendering of form values regardless of milestone status
  renderFormValues: z.boolean().optional(),
  // When false, do not draw outlines around empty optional fields (e.g. for PDFs being sent).
  // When undefined, defaults to same as renderFormValues (show outlines when form is being edited).
  drawBoxOutlines: z.boolean().optional(),
})
export type PayloadLienRightsMilestone = z.infer<typeof payloadLienRightsMilestoneSchema>

const payloadProjectFormsPreviewSchema = z.object({
  type: z.literal('projectFormsPreview'),
  contractId: z.guid(),
})
export type PayloadProjectFormsPreview = z.infer<typeof payloadProjectFormsPreviewSchema>

const payloadProjectLienWaiversPreviewSchema = z.object({
  type: z.literal('projectLienWaiversPreview'),
  contractId: z.guid(),
  lienWaiverType: z.union([z.literal('vendors'), z.literal('primary'), z.literal('subTier')]),
})
export type PayloadProjectLienWaiversPreview = z.infer<
  typeof payloadProjectLienWaiversPreviewSchema
>

const payloadChangeOrderRequestSchema = z.object({
  type: z.literal('changeOrderRequest'),
  changeOrderRequestId: z.guid(),
})
export type PayloadChangeOrderRequest = z.infer<typeof payloadChangeOrderRequestSchema>

const payloadChangeOrderLogSchema = z.object({
  type: z.literal('changeOrderLog'),
  contractId: z.guid(),
  payAppId: z.guid().optional(),
  includeVisibleToGcOnly: z.boolean().optional(),
  changeOrderLogTemplateVersionId: z.guid().optional(),
})
export type PayloadChangeOrderLog = z.infer<typeof payloadChangeOrderLogSchema>

const payloadPackageSchema = z.union([
  payloadPayAppSchema,
  payloadInvoiceSchema,
  payloadBackupSchema,
  payloadLienWaiversSchema,
  payloadLienWaiverSchema,
  payloadVendorLienWaiverPreviewSchema,
  payloadUnconditionalLienWaiverSchema,
  payloadLegalDocumentsSchema,
  payloadVendorLegalDocumentsSchema,
  payloadLegalDocumentSchema,
  payloadLegalRequirementSchema,
  payloadIntegrationSyncSchema,
  payloadTemplatesPreviewSchema,
  payloadLienRightsMilestoneSchema,
  payloadProjectFormsPreviewSchema,
  payloadProjectLienWaiversPreviewSchema,
  payloadChangeOrderRequestSchema,
  payloadChangeOrderLogSchema,
])
export type PayloadPackage = z.infer<typeof payloadPackageSchema>

export const payloadSchema = z.object({
  package: payloadPackageSchema,
})
export type Payload = z.infer<typeof payloadSchema>

// DEPRECATED: These fields are now directly accessible in the FormAnnotation type.
// We keep the type around for backwards compatibility with old clients.
export type FormTemplateAnnotationMetadata = {
  // Whether the annotation must have a value.
  isOptional?: boolean

  // Only for type TEMPLATE_VARIABLE. Indicates the template variable path/key.
  selectedKey?: string

  // Only for type USER_ENTERED_FIELD. Indicates the template variable path/key to use as a
  // default annotation value.
  defaultValueKey?: string

  // Only for type TEMPLATE_VARIABLE. Text to insert before the template variable.
  prefix?: string

  // Only for type TEMPLATE_VARIABLE. Text to insert after the template variable.
  suffix?: string

  // Only for type USER_ENTERED_FIELD. Known type of an annotation.
  // Annotations are opaque by default and we don't know what they mean. It makes it difficult to
  // make sense of what an annotation value represents – which is fine for most annotations but
  // not when we need to extract things like "lien-waiver amount".
  // To workaround that, `metadata.fieldType` allows to tag specific annotations for meaningful
  // processing of annotation values.
  //
  // This field is an enum with the following cases:
  //  - LIEN_WAIVER_AMOUNT
  fieldType?: FormTemplateAnnotationMetadataFieldType

  // Only for type USER_ENTERED_FIELD. Must also have defaultValueKey set. Indicates whether
  // this value should always be cleared when retaining annotations.
  doNotRetainOnReset?: boolean

  // Used for dynamic templates to match annotations to template tags.
  // Usage in XLSX/DOCX: {$field}
  // Usage in annotation metadata: field
  dynamicFieldTag?: string

  // Only for type USER_ENTERED_FIELD. Indicates whether the default value of this annotation
  // should be copied from the previous annotation value (using the permanent ID).
  copyDefaultValueFromPreviousAnnotationValue?: boolean

  // Only for type FormTemplateAnnotationImageType: type of image to render.
  imageType?: FormTemplateAnnotationImageType
}

export type Annotation = {
  id: string
  syncTag: string | null
  fontColor: string
  fontFamily: FormTemplateFont
  formTemplateVersionId: string
  height: number
  pageNumber: number
  type: FormTemplateAnnotationType
  signatureType: SignatureAnnotationType | null
  userVisibleName: string
  width: number
  xStart: number
  yStart: number
  defaultValue?: string
  textAlignment: TextAlignment
  isOptional: boolean
  selectedKey: string | null
  defaultValueKey: string | null
  prefix: string | null
  suffix: string | null
  fieldType: FormTemplateAnnotationMetadataFieldType | null
  doNotRetainOnReset: boolean
  dynamicFieldTag: string | null
  copyDefaultValueFromPreviousAnnotationValue: boolean
  imageType: FormTemplateAnnotationImageType | null
}

// Need to keep this for backwards compatibility with old clients
export type OldAnnotation = Annotation & {
  metadata: FormTemplateAnnotationMetadata
}

export type PageMetadata = {
  userVisibleName: string
  annotations: Annotation[]
  useCompanyNotarySignatureIfAvailable: boolean
  isAudit: boolean
  payAppId: string | null
  lienWaiverId: string | null
  requirementId: string | null
  formTemplateId: string | null
}

export type TemplateVariables = typeof templateVariablesDocumentation & {
  targetPayApp?: Record<string, unknown>
}

export type SimilarTemplate = {
  templateId: string
  versionId: string
  score: number
}

// Output of the PDF service's `/extractFormData` route. Coordinates are PDF points
// with a top-left origin to match how we lay out form-template annotations.
type FormExtractionBBox = {
  x0: number
  top: number
  x1: number
  bottom: number
  width: number
  height: number
}

// Heuristically-inferred fillable areas for flat PDFs (PDFs without AcroForm widgets).
// `kind` indicates which geometry source produced the candidate. `label` is the nearest
// text to the left or directly above the candidate, with trailing punctuation stripped.
// `confidence` is in [0, 1] when the producer has a meaningful score, or `null` when
// no signal is available (e.g. Reducto's /edit endpoint doesn't expose per-field
// confidence today). Consumers should treat `null` as "unknown", not "low".
type FormExtractionFillableFieldKind = 'underline' | 'rect' | 'tableCell'

export type FormExtractionFillableField = FormExtractionBBox & {
  kind: FormExtractionFillableFieldKind
  label: string | null
  description: string | null
  labelBBox: FormExtractionBBox | null
  confidence: number | null
}

export type FormExtractionPage = {
  index: number
  width: number
  height: number
  rotation: number
  text: string
  fillableFields: FormExtractionFillableField[]
}

export type FormExtraction = {
  schemaVersion: number
  pageCount: number
  metadata: Record<string, string | null>
  pages: FormExtractionPage[]
}

export type TemplateVariableLookup = {
  formTemplate: {
    id: string
    type: FormTemplateType
    userVisibleName: string
  }
  formTemplateVersion: {
    id: string
    fileType: StoredFileType
    versionNumber: number
  }
  formTemplateAnnotations: {
    id: string
  }[]
}
